import { Component } from "react";
import type { ErrorInfo } from "react";
import type { Props, State } from "@/types";
import * as Sentry from "@sentry/react";

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    /* c8 ignore next 3 */
    if (import.meta.env.DEV) {
      console.error("Error caught by boundary:", error, errorInfo);
    }

    /* c8 ignore next 3 */
    if (import.meta.env.PROD) {
      this.reportErrorToService(error, errorInfo);
    }
  }

  private reportErrorToService(error: Error, errorInfo: ErrorInfo) {
    Sentry.captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack,
        url: globalThis.location?.href || "unknown",
        userAgent: globalThis.navigator?.userAgent || "unknown",
        timestamp: new Date().toISOString(),
      },
    });
  }

  private readonly handleRefresh = (): void => {
    if (globalThis.location) {
      globalThis.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="min-h-screen flex items-center justify-center bg-background text-foreground p-6"
          role="alert"
          aria-live="assertive"
        >
          <div className="max-w-md text-center">
            <h1 className="text-4xl font-display font-bold mb-4 text-primary">
              <span aria-hidden="true">⚠️</span>
              <span className="sr-only">Application Error</span>
            </h1>
            <p className="text-muted-foreground mb-6">
              Something went wrong. Please refresh the page or try again later.
            </p>
            <button
              onClick={this.handleRefresh}
              className="px-6 py-3 bg-primary text-primary-foreground rounded-sm font-body font-semibold hover:opacity-90 transition-opacity"
              aria-label="Refresh the page to try again"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
